import { useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import type { LonLat } from '../api/client';
import { OSM_ATTRIBUTION, START_ANSICHT, TILE_STYLE_URL } from './config';
import { PunkteLayer, type KartenPunkt, type ZugLage } from './punkteLayer';
import { RouteZeichner } from './routeZeichner';

/** auswaehlen: Route nur ansehen · bearbeiten: Punkte verschieben/einfügen · route: neu zeichnen */
export type Werkzeug = 'auswaehlen' | 'bearbeiten' | 'route';

interface Props {
  route?: LonLat[] | null;
  /** Route mit Terra Draw bearbeitbar (sonst nur angezeigt) */
  routeBearbeitbar?: boolean;
  punkte?: KartenPunkt[];
  ausgewaehltId?: string | null;
  werkzeug?: Werkzeug;
  zug?: ZugLage | null;
  /** Druckansicht: nicht bedienbar, Bild bleibt für den Druck erhalten, Ausschnitt auf die Route */
  statisch?: boolean;
  /** Fadenkreuz-Cursor; Klick liefert die Koordinate (z. B. Sperrstelle setzen) */
  punktWaehlen?: boolean;
  onKartenKlick?: (p: LonLat) => void;
  onRouteAenderung?: (coords: LonLat[]) => void;
  onRouteGezeichnet?: () => void;
  onAuswahl?: (id: string) => void;
  popupInhalt?: (id: string) => HTMLElement | null;
  onBereit?: () => void;
}

export function MapView(props: Props) {
  const {
    route = null,
    routeBearbeitbar = false,
    punkte = [],
    ausgewaehltId = null,
    werkzeug = 'auswaehlen',
    zug = null,
    statisch = false,
    punktWaehlen = false,
  } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const zeichnerRef = useRef<RouteZeichner | null>(null);
  const layerRef = useRef<PunkteLayer | null>(null);
  const [bereit, setBereit] = useState(false);

  // Rückrufe über Refs, damit die Karte nicht bei jedem Rendern neu aufgebaut wird.
  const rueckrufe = useRef(props);
  rueckrufe.current = props;

  useEffect(() => {
    if (!containerRef.current) return;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: TILE_STYLE_URL,
      center: START_ANSICHT.center,
      zoom: START_ANSICHT.zoom,
      attributionControl: false,
      interactive: !statisch,
      ...(statisch ? { preserveDrawingBuffer: true, fadeDuration: 0 } : {}),
    } as maplibregl.MapOptions);
    mapRef.current = map;
    map.addControl(
      new maplibregl.AttributionControl({ compact: false, customAttribution: OSM_ATTRIBUTION }),
      'bottom-right',
    );
    if (!statisch)
      map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    map.addControl(new maplibregl.ScaleControl({ unit: 'metric' }), 'bottom-left');
    if (import.meta.env.DEV) {
      // Nur Entwicklung: Karte für Fehlersuche und E2E-Tests erreichbar machen.
      (window as unknown as { __karte?: maplibregl.Map }).__karte = map;
    }

    // 'style.load' statt 'load': Inhalte erscheinen, sobald der Stil da ist – ohne auf alle Kacheln zu warten.
    map.once('style.load', () => {
      zeichnerRef.current = new RouteZeichner(
        map,
        (coords) => rueckrufe.current.onRouteAenderung?.(coords),
        () => rueckrufe.current.onRouteGezeichnet?.(),
      );
      layerRef.current = new PunkteLayer(
        map,
        (id) => {
          // Beim Zeichnen landen Klicks oft auf Markern – dann nichts auswählen.
          if (rueckrufe.current.werkzeug === 'auswaehlen' && !rueckrufe.current.punktWaehlen)
            rueckrufe.current.onAuswahl?.(id);
        },
        (id) => rueckrufe.current.popupInhalt?.(id) ?? null,
      );
      setBereit(true);
    });
    map.on('click', (e) => {
      if (rueckrufe.current.punktWaehlen) {
        rueckrufe.current.onKartenKlick?.([round7(e.lngLat.lng), round7(e.lngLat.lat)]);
      }
    });
    if (statisch) map.once('idle', () => rueckrufe.current.onBereit?.());

    return () => {
      zeichnerRef.current?.beenden();
      zeichnerRef.current = null;
      layerRef.current = null;
      mapRef.current = null;
      map.remove();
    };
  }, [statisch]);

  useEffect(() => {
    if (!bereit) return;
    zeichnerRef.current?.setzen(routeBearbeitbar ? route : null);
    layerRef.current?.route(routeBearbeitbar ? null : route);
  }, [bereit, route, routeBearbeitbar]);

  // Ausschnitt einmal auf die Route bzw. die Punkte setzen
  const ausschnittGesetzt = useRef(false);
  useEffect(() => {
    const map = mapRef.current;
    if (!bereit || !map || ausschnittGesetzt.current) return;
    const alle: LonLat[] = [...(route ?? []), ...punkte.map((p) => p.position)];
    if (alle.length < 2) return;
    const b = new maplibregl.LngLatBounds(alle[0], alle[0]);
    alle.forEach((p) => b.extend(p));
    map.fitBounds(b, { padding: statisch ? 40 : 60, duration: 0, maxZoom: 17 });
    ausschnittGesetzt.current = true;
  }, [bereit, route, punkte, statisch]);

  useEffect(() => {
    if (!bereit) return;
    if (werkzeug === 'route' && routeBearbeitbar) zeichnerRef.current?.zeichnen();
    else zeichnerRef.current?.auswaehlen();
  }, [bereit, werkzeug, routeBearbeitbar]);

  useEffect(() => {
    if (bereit) layerRef.current?.setzen(punkte, ausgewaehltId, !statisch);
  }, [bereit, punkte, ausgewaehltId, statisch]);

  useEffect(() => {
    if (bereit) layerRef.current?.zug(zug);
  }, [bereit, zug]);

  useEffect(() => {
    const map = mapRef.current;
    if (map) map.getCanvas().style.cursor = punktWaehlen ? 'crosshair' : '';
  }, [punktWaehlen, bereit]);

  return (
    <div className={`karte${statisch ? ' karte--statisch' : ''}`}>
      <div
        ref={containerRef}
        className="karte__container"
        role="region"
        aria-label="Karte"
        data-testid="karte"
      />
    </div>
  );
}

function round7(v: number): number {
  return Math.round(v * 1e7) / 1e7;
}
